/**
 * Centralized API Mode Detection Utility
 *
 * SINGLE SOURCE OF TRUTH for determining API mode across the entire frontend.
 * This prevents inconsistencies that caused the "Backend Service Required"
 * warning to appear in staging environment.
 *
 * USE THIS INSTEAD OF:
 * - Direct `import.meta.env.VITE_API_MODE` comparisons
 * - Duplicated logic in multiple files
 * - Inconsistent environment checks
 */

import { STORAGE_KEYS } from '../constants/storage';

export type APIMode = 'local' | 'serverless';

/**
 * Check if running in Electron desktop mode.
 */
function isElectronMode(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { electronAPI?: { isElectron?: boolean } }).electronAPI?.isElectron ===
      true
  );
}

/**
 * Determine the current API mode.
 *
 * Returns 'serverless' when:
 * - VITE_API_MODE is explicitly 'serverless', OR
 * - VITE_ENVIRONMENT is 'production', OR
 * - VITE_ENVIRONMENT is 'staging'
 *
 * Returns 'local' when:
 * - Running in Electron desktop mode, OR
 * - VITE_API_MODE is 'local' or 'auto', OR
 * - Default fallback for development
 */
export function getAPIMode(): APIMode {
  // Electron desktop mode always uses local Flask API
  if (isElectronMode()) {
    return 'local';
  }

  const apiMode = import.meta.env.VITE_API_MODE;
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Explicit serverless mode
  if (apiMode === 'serverless') {
    return 'serverless';
  }

  // Production or staging environments default to serverless
  // This handles cases where VITE_API_MODE might not be set
  if (environment === 'production' || environment === 'staging') {
    return 'serverless';
  }

  // Explicit local mode
  if (apiMode === 'local' || apiMode === 'auto') {
    return 'local';
  }

  // Default to local for development
  return 'local';
}

/**
 * Check if the app is in serverless mode (production or staging).
 *
 * Use this for:
 * - Hiding local-only UI elements (e.g., "start Flask server" warning)
 * - Enabling serverless-specific features
 * - Retry logic for Cloud Run cold starts
 */
export function isServerlessMode(): boolean {
  return getAPIMode() === 'serverless';
}

/**
 * Check if the app is in local development mode.
 *
 * Use this for:
 * - Showing local development warnings
 * - Enabling development-only features
 * - Using Flask local API
 */
export function isLocalMode(): boolean {
  return getAPIMode() === 'local';
}

/**
 * Check if the app is running in Electron desktop mode.
 *
 * Exported for use cases that specifically need to know about Electron.
 */
export { isElectronMode };

/**
 * Check if we're running in E2E test mode.
 *
 * E2E test mode is detected via:
 * 1. URL query parameter '?e2e=true' (bootstrap on fresh tab load) — on detect,
 *    the flag is written to sessionStorage for the lifetime of the tab.
 * 2. sessionStorage key 'e2e_test_mode' set to 'true' (subsequent reads within
 *    the same tab after the URL bootstrap has run).
 *
 * IMPORTANT: the flag lives in sessionStorage, NOT localStorage.
 * localStorage.clear() (used in test beforeEach blocks to reset app state) does
 * NOT affect sessionStorage, so the harness bypass survives the clear.
 * See: project_e2e_harness_storage_isolation memory entry.
 */
export function isE2ETestMode(): boolean {
  if (typeof window === 'undefined') return false;

  // URL bootstrap: read ?e2e=true first; write sessionStorage for subsequent reads.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('e2e') === 'true') {
      try {
        sessionStorage.setItem(STORAGE_KEYS.E2E_TEST_MODE, 'true');
      } catch {
        // incognito / storage blocked — flag is still true for this call
      }
      return true;
    }
  } catch {
    // URL parsing failed
  }

  // Subsequent reads within the same tab hit sessionStorage.
  try {
    return sessionStorage.getItem(STORAGE_KEYS.E2E_TEST_MODE) === 'true';
  } catch {
    // sessionStorage not available (incognito restrictions, etc.)
    return false;
  }
}

/**
 * Check if we're in local development environment.
 *
 * Returns true when:
 * - Running in E2E test mode (e2e=true URL param or sessionStorage), OR
 * - Running in Electron desktop mode, OR
 * - Running in local API mode, OR
 * - VITE_ENVIRONMENT is not 'production' or 'staging'
 *
 * Use this for:
 * - Bypassing authentication (Firebase doesn't support localhost)
 * - Enabling development-only features
 * - Allowing E2E tests to run without login
 */
export function isLocalDevelopment(): boolean {
  if (isE2ETestMode()) return true;
  if (isElectronMode()) return true;
  if (isLocalMode()) return true;
  const env = import.meta.env.VITE_ENVIRONMENT;
  return env !== 'production' && env !== 'staging';
}

/**
 * Alias for isElectronMode() - checks if app is running as desktop application.
 *
 * Use this for:
 * - Desktop-specific UI (e.g., "Show in Folder" button)
 * - Using Electron APIs (e.g., window.electronAPI)
 */
export function isDesktopApp(): boolean {
  return isElectronMode();
}
